import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useIdempotencyKey } from '@/api/idempotency'
import { useCreateReturn } from '@/api/returns'
import type { ReturnBody } from '@/api/returns'
import { useAllStock } from '@/api/stock'
import { useWorkspace } from '@/api/workspace'
import { blockingItemsOf } from '@/components/corrections/blocking-items'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatQty } from '@/components/materials/pool-format'
import { needsBackdateReason, returnErrorMessage } from '@/components/returns/error-message'
import { SearchCombobox } from '@/components/search-combobox'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Create a Return (UI-SCREENS I-08, 05d #172). Phone first: one card per line, 44px targets,
// numeric keyboards. Each line shows what the chosen channel holds of that Variant right now, and
// a quantity above it is refused here and, under the balance lock, by the server. `?channelId=`
// prefills the channel, so the Return action on the Sales Channel screen is one tap away.
const searchSchema = z.object({
  channelId: z.coerce.number().int().positive().optional(),
})

export const Route = createFileRoute('/w/$slug/returns/new')({
  component: NewReturn,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'returns.form.title' },
})

// A Variant that can come back, with what each listed channel holds right now (the Stock view's
// figures, keyed by Sales Channel id).
type Item = { id: number; sku: string; name: string; perChannel: Record<number, number> }

function NewReturn() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const { channelId } = Route.useSearch()
  const workspace = useWorkspace(slug)
  const stock = useAllStock(slug)
  const rows = stock.data?.items
  const items: Item[] | undefined = rows?.map((row) => ({
    id: row.variant.id,
    sku: row.variant.sku,
    name: row.variant.name,
    perChannel: Object.fromEntries(row.stock.channels.map((channel) => [channel.sales_channel_id, channel.qty])),
  }))
  // The Stock view lists every non-archived channel plus any archived one that still holds stock,
  // and an archived channel keeps taking Returns until its stock is 0 (#169 Q2) — exactly the
  // channels a Return may come from. Every row carries the same list.
  const channels = (rows?.[0]?.stock.channels ?? []).map((channel) => ({
    id: channel.sales_channel_id,
    name: channel.name,
  }))

  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <Link to="/w/$slug/returns" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('returns.back')}
      </Link>
      <h1 className="font-heading text-xl font-semibold">{t('returns.form.title')}</h1>

      {workspace.isPending || stock.isPending ? (
        <LoadingSkeleton variant="form" rows={4} />
      ) : workspace.isError || !workspace.data || stock.isError || !items ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : !workspace.data.adopted_on ? (
        <Missing message={t('returns.errors.adoptionDateNotSet')} />
      ) : channels.length === 0 ? (
        <Missing message={t('returns.form.noChannels')} />
      ) : items.length === 0 ? (
        <Missing message={t('returns.form.noVariants')} />
      ) : (
        <ReturnForm
          slug={slug}
          adoptedOn={workspace.data.adopted_on}
          timezone={workspace.data.timezone}
          channels={channels}
          items={items}
          prefilledChannelId={channels.some((channel) => channel.id === channelId) ? channelId : undefined}
        />
      )}
    </section>
  )
}

function Missing({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="py-4">
        <p role="status" className="text-sm">
          {message}
        </p>
      </CardContent>
    </Card>
  )
}

const EMPTY_LINE = { variantId: '', qty: '' }

// Today in the Workspace's timezone: the business date may never be after it (domain-rules).
function todayIn(timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date())
}

function ReturnForm({
  slug,
  adoptedOn,
  timezone,
  channels,
  items,
  prefilledChannelId,
}: {
  slug: string
  adoptedOn: string
  timezone: string
  channels: { id: number; name: string }[]
  items: Item[]
  prefilledChannelId?: number
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const create = useCreateReturn()
  const idempotency = useIdempotencyKey()
  // The backdate limit lives on the server; the reason field appears once it asks for one.
  const [askReason, setAskReason] = useState(false)
  const today = todayIn(timezone)

  // Every stored number is an integer (domain-rules); Stock at Channel never goes negative, so a
  // quantity above what the channel holds is refused here too — the server re-checks it under the
  // balance lock.
  const formSchema = z
    .object({
      channelId: z.string().min(1, { message: t('returns.form.required') }),
      occurredOn: z.string().min(1, { message: t('returns.form.required') }),
      note: z.string(),
      backdateReason: z.string(),
      lines: z
        .array(z.object({ variantId: z.string().min(1, { message: t('returns.form.required') }), qty: z.string() }))
        .min(1),
    })
    .superRefine((values, context) => {
      const channel = Number(values.channelId)
      values.lines.forEach((line, index) => {
        if (!/^\d+$/.test(line.qty) || Number(line.qty) < 1) {
          context.addIssue({ code: 'custom', path: ['lines', index, 'qty'], message: t('returns.form.qtyInvalid') })
          return
        }
        const item = items.find((candidate) => String(candidate.id) === line.variantId)
        if (item && Number(line.qty) > (item.perChannel[channel] ?? 0)) {
          context.addIssue({
            code: 'custom',
            path: ['lines', index, 'qty'],
            message: t('returns.form.aboveChannel', { qty: formatQty(item.perChannel[channel] ?? 0) }),
          })
        }
      })
    })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      channelId: prefilledChannelId ? String(prefilledChannelId) : String(channels[0].id),
      occurredOn: today,
      note: '',
      backdateReason: '',
      lines: [EMPTY_LINE],
    },
  })
  const lines = useFieldArray({ control: form.control, name: 'lines' })
  // useWatch rather than form.watch: it returns a value the React compiler can memoize.
  const watched = useWatch({ control: form.control, name: 'lines' })
  const watchedChannel = useWatch({ control: form.control, name: 'channelId' })
  // The first stock item a refused save named: it opens the Adjustment form at that channel
  // (I-11, 05e #173); the rest are named in the message above.
  const blockedItemId = create.isError ? blockingItemsOf(create.error)[0]?.stock_item_id : undefined

  const onSubmit = form.handleSubmit((values) => {
    const backdateReason = values.backdateReason.trim()
    const body: ReturnBody = {
      return: {
        sales_channel_id: Number(values.channelId),
        occurred_on: values.occurredOn,
        note: values.note.trim() === '' ? null : values.note.trim(),
        ...(backdateReason === '' ? {} : { backdate_reason: backdateReason }),
        lines: values.lines.map((line) => ({ variant_id: Number(line.variantId), qty: Number(line.qty) })),
      },
    }
    create.mutate(
      { params: { path: { slug }, header: idempotency.headerFor(body) }, body },
      {
        onSuccess: (data) => navigate({ to: '/w/$slug/returns/$returnId', params: { slug, returnId: String(data.id) } }),
        onSettled: (_data, error) => {
          idempotency.settle(error)
          if (needsBackdateReason(error)) setAskReason(true)
        },
      },
    )
  })

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {create.isError ? returnErrorMessage(t, create.error) : ''}
      </p>
      {blockedItemId ? (
        <Button asChild variant="outline" className="h-11 self-start">
          <Link
            to="/w/$slug/adjustments/new"
            params={{ slug }}
            search={{ location: 'channel', channelId: Number(watchedChannel), itemId: blockedItemId }}
          >
            {t('returns.form.toAdjustment')}
          </Link>
        </Button>
      ) : null}

      <div className="flex flex-col gap-2">
        <Label htmlFor="channelId">{t('returns.form.channel')}</Label>
        <NativeSelect id="channelId" {...form.register('channelId')}>
          {channels.map((channel) => (
            <option key={channel.id} value={String(channel.id)}>
              {channel.name}
            </option>
          ))}
        </NativeSelect>
        <FieldError message={form.formState.errors.channelId?.message} />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="occurredOn">{t('returns.form.date')}</Label>
        <Input
          id="occurredOn"
          type="date"
          className="h-11"
          min={adoptedOn}
          max={today}
          {...form.register('occurredOn')}
        />
        <p className="text-sm text-muted-foreground">{t('returns.form.dateHint')}</p>
        <FieldError message={form.formState.errors.occurredOn?.message} />
      </div>

      {askReason ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="backdateReason">{t('returns.form.backdateReason')}</Label>
          <Input id="backdateReason" className="h-11" {...form.register('backdateReason')} />
          <p className="text-sm text-muted-foreground">{t('returns.form.backdateReasonHint')}</p>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        {lines.fields.map((field, index) => (
          <Card key={field.id}>
            <CardContent className="flex flex-col gap-3 py-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor={`variant-${index}`}>{t('returns.form.variant')}</Label>
                <Controller
                  control={form.control}
                  name={`lines.${index}.variantId` as const}
                  render={({ field, fieldState }) => (
                    <SearchCombobox
                      id={`variant-${index}`}
                      value={field.value}
                      options={items.map((item) => ({ value: String(item.id), code: item.sku, name: item.name }))}
                      invalid={Boolean(fieldState.error)}
                      placeholder={t('returns.form.selectVariant')}
                      searchPlaceholder={t('searchCombobox.variant.search')}
                      onChange={field.onChange}
                    />
                  )}
                />
                <FieldError message={form.formState.errors.lines?.[index]?.variantId?.message} />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor={`qty-${index}`}>{t('returns.form.qty')}</Label>
                <Input id={`qty-${index}`} inputMode="numeric" className="h-11" {...form.register(`lines.${index}.qty`)} />
                <p className="text-sm text-muted-foreground">
                  {t('returns.form.channelStock', { qty: formatQty(atChannel(watched[index], items, watchedChannel)) })}
                </p>
                <FieldError message={form.formState.errors.lines?.[index]?.qty?.message} />
              </div>

              {lines.fields.length > 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="h-11 self-start text-destructive"
                  onClick={() => lines.remove(index)}
                >
                  <Trash2 aria-hidden="true" />
                  {t('returns.form.removeLine')}
                </Button>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>

      <Button type="button" variant="outline" className="h-11 self-start" onClick={() => lines.append(EMPTY_LINE)}>
        {t('returns.form.addLine')}
      </Button>

      <div className="flex flex-col gap-2">
        <Label htmlFor="note">{t('returns.form.note')}</Label>
        <Input id="note" className="h-11" {...form.register('note')} />
        <FieldError message={form.formState.errors.note?.message} />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="h-11" disabled={create.isPending}>
          {t(create.isPending ? 'returns.form.saving' : 'returns.form.save')}
        </Button>
        <Button asChild variant="ghost" className="h-11">
          <Link to="/w/$slug/returns" params={{ slug }}>
            {t('returns.form.cancel')}
          </Link>
        </Button>
      </div>
    </form>
  )
}

// What the chosen channel holds of the line's Variant right now.
const atChannel = (line: { variantId: string } | undefined, items: Item[], channelId: string) =>
  items.find((item) => String(item.id) === line?.variantId)?.perChannel[Number(channelId)] ?? 0

function NativeSelect({ className, ...props }: React.ComponentProps<'select'>) {
  return (
    <select
      className={`h-11 w-full rounded-lg border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 ${className ?? ''}`}
      {...props}
    />
  )
}

function FieldError({ message }: { message?: string }) {
  return (
    <p role="alert" className="text-sm text-destructive empty:hidden">
      {message ?? ''}
    </p>
  )
}
